import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as clientSupportService from "@features/client-support/services/clientSupportService";
import { toast } from "sonner";

// ========================================
// QUERY HOOKS
// ========================================

/**
 * Hook to fetch all client support users
 * Admin/Manager only
 */
export const useClientSupportQuery = () => {
  return useQuery({
    queryKey: ["clientSupport", "all"],
    queryFn: clientSupportService.getAllClientSupport,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch assigned clients for a support user
 */
export const useAssignedClientsQuery = (supportId, params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", supportId, "assignedClients", params],
    queryFn: () => clientSupportService.getAssignedClients(supportId, params),
    enabled: !!supportId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch unassigned clients
 * Admin/Manager only
 */
export const useUnassignedClientsQuery = (params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", "unassignedClients", params],
    queryFn: () => clientSupportService.getUnassignedClients(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch support user details
 */
export const useSupportUserDetailsQuery = (supportId) => {
  return useQuery({
    queryKey: ["clientSupport", supportId, "details"],
    queryFn: () => clientSupportService.getSupportUserDetails(supportId),
    enabled: !!supportId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch dashboard statistics for CLIENT SUPPORT user
 */
export const useClientSupportDashboardStats = (supportId) => {
  return useQuery({
    queryKey: ["clientSupport", supportId, "dashboardStats"],
    queryFn: () =>
      clientSupportService.getClientSupportDashboardStats(supportId),
    enabled: !!supportId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to fetch packages for assigned clients
 */
export const useAssignedClientsPackages = (params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", "packages", params],
    queryFn: () => clientSupportService.getAssignedClientsPackages(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch tickets for assigned clients
 */
export const useAssignedClientsTickets = (params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", "tickets", params],
    queryFn: () => clientSupportService.getAssignedClientsTickets(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch invoices for assigned clients
 */
export const useAssignedClientsInvoices = (params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", "invoices", params],
    queryFn: () => clientSupportService.getAssignedClientsInvoices(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch pickups for assigned clients
 */
export const useAssignedClientsPickups = (params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", "pickups", params],
    queryFn: () => clientSupportService.getAssignedClientsPickups(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch return vouchers for assigned clients
 */
export const useAssignedClientsReturnVouchers = (params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", "returnVouchers", params],
    queryFn: () =>
      clientSupportService.getAssignedClientsReturnVouchers(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch products for assigned clients
 */
export const useAssignedClientsProducts = (params = {}) => {
  return useQuery({
    queryKey: ["clientSupport", "products", params],
    queryFn: () => clientSupportService.getAssignedClientsProducts(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch specific client details
 */
export const useClientDetails = (clientId) => {
  return useQuery({
    queryKey: ["clients", clientId],
    queryFn: () => clientSupportService.getClientDetails(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ========================================
// MUTATION HOOKS
// ========================================

/**
 * Hook to assign clients to a support user
 * Admin/Manager only
 */
export const useAssignClientsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ supportId, clientIds }) =>
      clientSupportService.assignClients(supportId, clientIds),
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({
        queryKey: ["clientSupport", variables.supportId],
      });
      queryClient.invalidateQueries({
        queryKey: ["clientSupport", "unassignedClients"],
      });
      queryClient.invalidateQueries({
        queryKey: ["clientSupport", "all"],
      });

      toast.success(data.message || "Clients assigned successfully", {
        description: `${variables.clientIds.length} client(s) assigned`,
      });
    },
    onError: (error) => {
      toast.error("Failed to assign clients", {
        description: error.response?.data?.message || error.message,
      });
    },
  });
};

/**
 * Hook to unassign clients from a support user
 * Admin/Manager only
 */
export const useUnassignClientsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ supportId, clientIds }) =>
      clientSupportService.unassignClients(supportId, clientIds),
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({
        queryKey: ["clientSupport", variables.supportId],
      });
      queryClient.invalidateQueries({
        queryKey: ["clientSupport", "unassignedClients"],
      });
      queryClient.invalidateQueries({
        queryKey: ["clientSupport", "all"],
      });

      toast.success(data.message || "Clients unassigned successfully", {
        description: `${variables.clientIds.length} client(s) unassigned`,
      });
    },
    onError: (error) => {
      toast.error("Failed to unassign clients", {
        description: error.response?.data?.message || error.message,
      });
    },
  });
};

// ========================================
// COMPOSITE HOOKS
// ========================================

/**
 * Composite hook for CLIENT SUPPORT assignment management
 * Combines all necessary queries and mutations for the management UI
 */
export const useClientAssignments = (supportId) => {
  const supportUsers = useClientSupportQuery();
  const assignedClients = useAssignedClientsQuery(supportId);
  const unassignedClients = useUnassignedClientsQuery();
  const supportUserDetails = useSupportUserDetailsQuery(supportId);
  const assignMutation = useAssignClientsMutation();
  const unassignMutation = useUnassignClientsMutation();

  return {
    // Queries
    supportUsers,
    assignedClients,
    unassignedClients,
    supportUserDetails,

    // Loading states
    isLoadingSupportUsers: supportUsers.isLoading,
    isLoadingAssignedClients: assignedClients.isLoading,
    isLoadingUnassignedClients: unassignedClients.isLoading,

    // Mutations
    assignClients: assignMutation.mutate,
    unassignClients: unassignMutation.mutate,
    isAssigning: assignMutation.isPending,
    isUnassigning: unassignMutation.isPending,

    // Refetch functions
    refetchSupportUsers: supportUsers.refetch,
    refetchAssignedClients: assignedClients.refetch,
    refetchUnassignedClients: unassignedClients.refetch,

    // Combined loading state
    isLoading:
      supportUsers.isLoading ||
      assignedClients.isLoading ||
      unassignedClients.isLoading,

    // Error handling
    error:
      supportUsers.error || assignedClients.error || unassignedClients.error,
  };
};

/**
 * Composite hook for CLIENT SUPPORT dashboard
 * Combines all data needed for the dashboard view
 */
export const useClientSupportDashboard = (supportId, params = {}) => {
  const assignedClients = useAssignedClientsQuery(supportId, {
    page: 1,
    limit: 100,
  });
  const packages = useAssignedClientsPackages(params.packagesParams || {});
  const tickets = useAssignedClientsTickets(params.ticketsParams || {});
  const invoices = useAssignedClientsInvoices(params.invoicesParams || {});

  return {
    assignedClients,
    packages,
    tickets,
    invoices,
    isLoading:
      assignedClients.isLoading ||
      packages.isLoading ||
      tickets.isLoading ||
      invoices.isLoading,
    error:
      assignedClients.error ||
      packages.error ||
      tickets.error ||
      invoices.error,
  };
};
